import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

export default function ReviewContent({ searchParams }) {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [checkAll, setCheckAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCategory1, setSelectedCategory1] = useState(0); // 서빙로봇(0), 청소로봇(1), 인터뷰(2)
  const [selectedCategory2, setSelectedCategory2] = useState(0); // 식당(0), 뷔페(1), 카페(2), 베이커리(3), 스크린골프(4), 병원(5), 호텔(6), 기타(7)

  // 카테고리 옵션들
  const category1Options = [
    { value: 0, label: '서빙로봇' },
    { value: 1, label: '청소로봇' },
    { value: 2, label: '인터뷰' },  // 물류로봇 → 인터뷰로 변경
  ];

  const category2Options = [
    { value: 0, label: '식당' },
    { value: 1, label: '뷔페' },
    { value: 2, label: '카페베이커리' },
    { value: 3, label: '스크린골프' },
    { value: 4, label: '병원' },
    { value: 5, label: '호텔' },
    { value: 6, label: '기타' },
  ];

  // 인터뷰가 선택되었는지 확인
  const isInterviewSelected = selectedCategory1 === 2;

  // 카테고리1 변경 핸들러
  const handleCategory1Change = (category) => {
    setSelectedCategory1(category);
    // 인터뷰가 선택되면 category2를 0으로 자동 설정
    if (category === 2) {
      setSelectedCategory2(0);
    }
    setCurrentPage(1); // 페이지를 1로 리셋
    setCheckedItems(new Set());
    setCheckAll(false);
    // 자동 검색 추가
    fetchData();
  };

  // 카테고리2 변경 핸들러 (인터뷰가 아닐 때만 작동)
  const handleCategory2Change = (category) => {
    if (selectedCategory1 !== 2) { // 인터뷰가 아닐 때만 변경 가능
      setSelectedCategory2(category);
      setCurrentPage(1); // 페이지를 1로 리셋
      setCheckedItems(new Set());
      setCheckAll(false);
      // 자동 검색 추가
      fetchData();
    }
  };

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    console.log('=== 검색 버튼 클릭됨 ===');
    setCurrentPage(1);
    setCheckedItems(new Set());
    setCheckAll(false);
    // fetchData 함수 직접 호출
    fetchData();
  };

  // 카테고리 텍스트 반환
  const getCategory1Text = (category) => {
    const found = category1Options.find((opt) => opt.value === category);
    return found ? found.label : '알 수 없음';
  };

  const getCategory2Text = (category) => {
    const found = category2Options.find((opt) => opt.value === category);
    return found ? found.label : '알 수 없음';
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('=== API 호출 시작 ===');
      
      // 인터뷰일 때는 강제로 category2를 0으로 설정
      const actualCategory2 = selectedCategory1 === 2 ? 0 : selectedCategory2;
      
      console.log('API 호출:', {
        category1: selectedCategory1,
        category2: actualCategory2,
        page: currentPage,
        keyword: searchParams?.keyword || '',
        searchType: searchParams?.searchType || '',
      });

      // 파라미터 객체 생성
      const apiParams = {
        curPage: currentPage,
      };

      // 검색 파라미터가 있을 때만 추가
      if (searchParams?.keyword && searchParams.keyword.trim()) {
        apiParams.keyword = searchParams.keyword.trim();
        apiParams.filter = searchParams.searchType || 'title';
        console.log('검색 조건 추가:', apiParams);
      }

      console.log('전송할 파라미터:', apiParams);

      // 정확한 API 경로 사용 (인터뷰일 때는 category2=0)
      const endpoint = `/api/admin/review/categories/${selectedCategory1}/${actualCategory2}`;

      console.log(`시도 중인 API: ${endpoint}`);

      const response = await axios.get(endpoint, {
        params: apiParams,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`성공한 API: ${endpoint}`, response.data);

      if (response.data && response.data.success !== false) {
        const reviewList = response.data.list || [];
        const paginationData = response.data.pagination || {};

        console.log('처리된 리뷰 리스트:', reviewList);
        console.log('페이지네이션 데이터:', paginationData);

        setReviews(reviewList);
        setTotalCount(paginationData.totalCount || 0);

        // 백엔드에서 온 페이지네이션 데이터 그대로 사용
        const totalPages = paginationData.totalPages || 1;
        const pageSize = paginationData.pageSize || 10;
        const currentPageForCalc = paginationData.currentPage || currentPage;
        const totalCount = paginationData.totalCount || 0;

        // 페이지 그룹 계산 (10페이지씩 그룹화)
        const pageGroup = Math.floor((currentPageForCalc - 1) / 10);
        const startPage = pageGroup * 10 + 1;
        const endPage = Math.min(startPage + 9, totalPages);

        // 목록 번호 계산 (역순)
        const startNo = totalCount - (currentPageForCalc - 1) * pageSize;

        const adminPagination = {
          pageCnt: totalPages,
          startPage: startPage,
          endPage: endPage,
          startNo: startNo,
          listCnt: totalCount,
          currentPage: currentPageForCalc,
          pageSize: pageSize,
        };

        setPagination(adminPagination);
        console.log('계산된 페이지네이션:', adminPagination);
        console.log('=== API 호출 완료 ===');
      } else {
        console.error('API 오류:', response.data);
        setError(response.data.error || 'API 호출에 실패했습니다');
      }
    } catch (error) {
      console.error('=== API 호출 실패 ===', error);

      let errorMessage = '데이터를 불러오는 중 오류 발생';

      if (error.response) {
        console.error('응답 상태:', error.response.status);
        console.error('응답 데이터:', error.response.data);
        errorMessage = `서버 오류 (${error.response.status}): ${
          error.response.data.message || error.response.statusText
        }`;
      } else if (error.request) {
        errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
      } else {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory1, selectedCategory2, currentPage, searchParams]);

  // 초기 로드 시 자동으로 카테고리 0, 0 데이터 조회
  useEffect(() => {
    console.log('=== 컴포넌트 초기 로드 - 자동 검색 실행 ===');
    fetchData();
  }, [fetchData]);

  // 페이지 변경 시에만 자동 호출
  useEffect(() => {
    if (currentPage > 1) {
      console.log('=== 페이지 변경으로 인한 자동 검색 ===');
      fetchData();
    }
  }, [currentPage, fetchData]);

  const handlePageChange = (pageNum) => {
    console.log('페이지 변경:', pageNum);
    setCurrentPage(pageNum);
  };

  const renderPagination = () => {
    if (!pagination || !pagination.pageCnt) {
      return null;
    }

    const pages = [];
    const pageCnt = pagination.pageCnt || 0;
    const startPage = pagination.startPage || 1;
    const endPage = pagination.endPage || 1;

    // 처음으로
    pages.push(
      <li key="first" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(1);
          }}
        >
          《
        </a>
      </li>
    );

    // 이전
    pages.push(
      <li key="prev" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(currentPage - 1);
          }}
        >
          〈
        </a>
      </li>
    );

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={i === currentPage ? 'num_on' : 'num'}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // 다음
    pages.push(
      <li key="next" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(currentPage + 1);
          }}
        >
          〉
        </a>
      </li>
    );

    // 마지막으로
    pages.push(
      <li key="last" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(pageCnt);
          }}
        >
          》
        </a>
      </li>
    );

    return pages;
  };

  const handleDelete = async () => {
    if (checkedItems.size === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (window.confirm('선택한 항목을 삭제하시겠습니까?')) {
      try {
        const deleteIds = Array.from(checkedItems);

        const response = await axios.post('/api/admin/review/delete', {
          ids: deleteIds,
        });

        if (response.data.success) {
          alert(response.data.message || '선택한 항목이 삭제되었습니다.');
          setCheckedItems(new Set());
          setCheckAll(false);
          // 현재 페이지 데이터 새로고침
          window.location.reload();
        } else {
          alert(response.data.message || '삭제 처리 중 오류가 발생했습니다.');
        }
      } catch (error) {
        console.error('삭제 오류:', error);
        alert(
          '삭제 중 오류가 발생했습니다: ' +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckAll(isChecked);

    if (isChecked) {
      const allIds = new Set(reviews.map((review) => review.r_no));
      setCheckedItems(allIds);
    } else {
      setCheckedItems(new Set());
    }
  };

  const handleCheckItem = (reviewId) => {
    const newCheckedItems = new Set(checkedItems);

    if (newCheckedItems.has(reviewId)) {
      newCheckedItems.delete(reviewId);
    } else {
      newCheckedItems.add(reviewId);
    }

    setCheckedItems(newCheckedItems);
    setCheckAll(newCheckedItems.size === reviews.length && reviews.length > 0);
  };

  if (loading) {
    return (
      <div className="txt-center" style={{ marginTop: '60px' }}>
        로딩 중...
      </div>
    );
  }

  return (
    <>
      {/* 카테고리1 필터 (로봇 종류) */}
      <div className="filter-section mt-2">
        <p className="prt-medium fs-18">카테고리</p>
        <div className="row-flex-center gap-20">
          {category1Options.map((option) => (
            <div key={option.value} className="row-flex-center gap-10 mt-2">
              <input
                type="radio"
                value={option.value}
                id={`category1_${option.value}`}
                name="category1"
                checked={selectedCategory1 === option.value}
                onChange={() => handleCategory1Change(option.value)}
              />
              <label
                htmlFor={`category1_${option.value}`}
                className="prt-regular fs-16 pointer"
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* 카테고리2 필터 (업종) - 인터뷰가 아닐 때만 표시 */}
      {!isInterviewSelected && (
        <div className="filter-section" style={{ marginTop: '20px' }}>
          <p className="prt-medium fs-18">업종</p>
          <div className="row-flex-center gap-20" style={{ flexWrap: 'wrap' }}>
            {category2Options.map((option) => (
              <div key={option.value} className="row-flex-center gap-10 mt-2">
                <input
                  type="radio"
                  value={option.value}
                  id={`category2_${option.value}`}
                  name="category2"
                  checked={selectedCategory2 === option.value}
                  onChange={() => handleCategory2Change(option.value)}
                />
                <label
                  htmlFor={`category2_${option.value}`}
                  className="prt-regular fs-16 pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 인터뷰 선택 시 안내 메시지 */}
      {isInterviewSelected && (
        <div className="filter-section" style={{ marginTop: '20px' }}>
          <p className="prt-medium fs-18">업종</p>
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            인터뷰 카테고리는 별도의 업종 분류가 없습니다.
          </div>
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div
          className="error-message"
          style={{ color: 'red', margin: '20px 0' }}
        >
          {error}
        </div>
      )}

      {/* 데이터 테이블 */}
      <div className="over_hi" style={{ marginTop: '60px' }}>
        <form id="deleteFrm" method="post">
          <table className="adminTable">
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '15%' }} />
              {!isInterviewSelected && <col style={{ width: '15%' }} />}
              <col style={{ width: isInterviewSelected ? '50%' : '35%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    id="checkall"
                    checked={checkAll}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>No</th>
                <th>카테고리</th>
                {!isInterviewSelected && <th>업종</th>}
                <th>제목</th>
                <th>작성자</th>
                <th>작성날짜</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 ? (
                <tr>
                  <td colSpan={isInterviewSelected ? "6" : "7"} className="txt-center">
                    {loading
                      ? '로딩 중...'
                      : '해당 조건의 설치사례가 없습니다.'}
                  </td>
                </tr>
              ) : (
                reviews.map((review, index) => {
                  const reviewId = review.r_no;
                  const reviewTitle = review.r_title;
                  const reviewCategory1 = review.r_category1;
                  const reviewCategory2 = review.r_category2;
                  const reviewDate = review.r_add_dt;

                  return (
                    <tr key={reviewId}>
                      <td>
                        <input
                          type="checkbox"
                          value={reviewId}
                          name="check"
                          checked={checkedItems.has(reviewId)}
                          onChange={() => handleCheckItem(reviewId)}
                        />
                      </td>
                      <td>
                        {pagination.startNo && pagination.startNo > 0
                          ? pagination.startNo - index
                          : (totalCount || 0) - (currentPage - 1) * 10 - index}
                      </td>
                      <td className="txt-center">
                        <span className="status-badge status-active">
                          {getCategory1Text(reviewCategory1)}
                        </span>
                      </td>
                      {!isInterviewSelected && (
                        <td className="txt-center">
                          <span className="status-badge status-ended">
                            {getCategory2Text(reviewCategory2)}
                          </span>
                        </td>
                      )}
                      <td className="left">
                        <a href={`/admin/review_d/${reviewId}`}>
                          {reviewTitle}
                        </a>
                      </td>
                      <td className="txt-center">관리자</td>
                      <td className="txt-center">
                        {reviewDate
                          ? format(new Date(reviewDate), 'yyyy/MM/dd HH:mm')
                          : ''}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </form>
      </div>

      {/* 페이지네이션 */}
      <ul className="txt-center pageing">{renderPagination()}</ul>

      {/* 액션 버튼들 */}
      <div className="row-flex flex-end gap-10">
        <input
          type="button"
          value="삭제"
          className="common-del"
          onClick={handleDelete}
        />
        <input
          type="button"
          value="글쓰기"
          className="common-del"
          onClick={() => (window.location.href = '/admin/review_w')}
        />
      </div>
    </>
  );
}